import { and, eq, getTableColumns, sql } from "drizzle-orm";

import type { WsFeedGroup } from "@storyboard/web-client";
import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { feedGroup, feedGroupMembership, permission } from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";

function transformWsFeedGroup(r: WsFeedGroup) {
  return {
    id: r.id,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt ?? null,
    workspaceId: r.workspaceId,
    name: r.name,
    enabled: r.enabled ? 1 : 0,
  };
}

export async function bulkUpsertFeedGroup(records: WsFeedGroup[]) {
  if (records.length === 0) return;
  const data = records.map((r) => transformWsFeedGroup(r));
  const deduped = deduplicateByKeyValue(data, (item) => item.id);
  await bulkInsert(feedGroup, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [feedGroup.id],
      set: {
        updatedAt: sql`"excluded"."updatedAt"`,
        workspaceId: sql`"excluded"."workspaceId"`,
        name: sql`"excluded"."name"`,
        enabled: sql`"excluded"."enabled"`,
        createdAt: sql`"excluded"."createdAt"`,
      },
    },
  });
  emitTableUpdates(["feed_group"]);
}

export function groupsQuery({
  workspaceId,
  myCurrentWorkspaceMembershipId,
}: {
  workspaceId?: string;
  myCurrentWorkspaceMembershipId?: string;
}) {
  const query = db
    .selectDistinct({
      ...getTableColumns(feedGroup),
    })
    .from(feedGroup)
    .innerJoin(feedGroupMembership, eq(feedGroup.id, feedGroupMembership.groupId))
    .innerJoin(
      permission,
      and(
        eq(permission.feedId, feedGroupMembership.feedId),
        eq(permission.name, "read"),
        eq(permission.enabled, true),
        eq(permission.workspaceMembershipId, myCurrentWorkspaceMembershipId),
      ),
    )
    .where(eq(feedGroup.workspaceId, workspaceId))
    .orderBy(feedGroup.name);

  if (!workspaceId || !myCurrentWorkspaceMembershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Groups", query);
  return query;
}
